// src/pages/TrilhaFormPage/TrilhaFormPage.jsx

import { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';

// Componentes
import Breadcrumbs from '../../components/Breadcrumbs/Breadcrumbs';
import FriendlyPhrase from '../../components/FriendlyPhrase/FriendlyPhrase';

// Contextos
import { useToast } from '../../contexts/ToastContext';
import { useLoader } from '../../contexts/LoaderContext';

// API
import { useTrilhaService } from '../../services/trilhaServices';
import { useModuloService } from '../../services/moduloServices';
import { useTesteService } from '../../services/testeServices';
import { useModuloSecaoService } from '../../services/moduloSecaoServices';
import { useModuloConteudoService } from '../../services/moduloConteudoServices';

// Constantes
import { URLS } from '../../constants/urls';

// Estilos
import styles from './trilhaformpage.module.css';

// Assets
import { FaArrowUp, FaArrowDown } from 'react-icons/fa';
import { FiX, FiEdit3, FiPlus, FiFileText } from 'react-icons/fi';

/** ==== Helpers gerais ==== */

const genRandomId = () => {
  try {
    if (window?.crypto?.randomUUID) return window.crypto.randomUUID();
  } catch {}
  return `ID_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
};

const genUniqueId = (existing = new Set()) => {
  let id = genRandomId();
  while (existing.has(id)) id = genRandomId();
  return id;
};

// Letras A..Z para opções
const reindexLetters = (opcoes) =>
  (opcoes || []).map((o, idx) => ({ ...o, letra: String.fromCharCode(65 + idx) }));

const nextAvailableLetter = (opcoes) => {
  const used = new Set((opcoes || []).map((o) => o.letra));
  for (let i = 0; i < 26; i++) {
    const L = String.fromCharCode(65 + i);
    if (!used.has(L)) return L;
  }
  return null;
};

// Tipos de pergunta do mini teste
const QUESTION_TYPES = ['closed', 'open'];
const QUESTION_TYPE_LABEL = {
  closed: 'Fechada',
  open: 'Aberta',
};

// Tipos de conteúdo da seção
const CONTENT_TYPES = ['texto', 'lista', 'imagem', 'link', 'video', 'pdf'];
const CONTENT_TYPE_LABEL = {
  texto: 'Texto',
  lista: 'Lista',
  imagem: 'Imagem',
  link: 'Link',
  video: 'Vídeo (YouTube / externo)',
  pdf: 'PDF',
};

// ✅ NOVO: Critério de conclusão do módulo (backend enum: livre|secoes)
const CRITERIO_CONCLUSAO_OPTIONS = ['secoes', 'livre'];
const CRITERIO_CONCLUSAO_LABEL = {
  secoes: 'Por seções (padrão)',
  livre: 'Livre (sem travas por seções)',
};

function TrilhaFormPage() {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { showLoader, hideLoader } = useLoader();

  const { idTrilha } = useParams();
  const { pathname } = useLocation();
  const isEditing = !!idTrilha && pathname.includes('atualizar');

  const { criarTrilha, atualizarTrilha, obterTrilhaPorId } = useTrilhaService();
  const { criarModulo, listarModulosPorTrilha, atualizarModulo, deletarModulo, publicarModulo } =
    useModuloService();
  const { listarTestes } = useTesteService();
  const { criarSecao, listarSecoesPorModulo, atualizarSecao, deletarSecao, publicarSecao } =
    useModuloSecaoService();
  const { criarConteudo, listarConteudosPorSecao, atualizarConteudo, deletarConteudo } =
    useModuloConteudoService();

  const [testes, setTestes] = useState([]);

  // ==== DADOS DA TRILHA ====
  const [trilhaData, setTrilhaData] = useState({
    titulo: '',
    descricao: '',
    objetivos: '',
    cargaHoraria: '',
    certificado: false,
    templateCertificadoFile: null, // arquivo novo selecionado
    templateCertificadoUrl: null, // arquivo já salvo no backend
  });

  // Quantidade de módulos solicitada
  const [qtdModulos, setQtdModulos] = useState(0);

  // Lista de módulos na tela
  const [modulos, setModulos] = useState([]);

  // Snapshot inicial de IDs existentes no backend ao carregar a página
  const [initialSnapshot, setInitialSnapshot] = useState({
    modulos: [],
    secoes: [],
    conteudos: [],
  });

  const [scrollBottom, setScrollBottom] = useState(false);

  // ===== MODAL DE EDIÇÃO DO MÓDULO =====
  const [isModuloModalOpen, setIsModuloModalOpen] = useState(false);
  const [activeModuloIndex, setActiveModuloIndex] = useState(null);

  // ===== Picker para adicionar seção já com conteúdo inicial =====
  const [isAddSecaoPickerOpen, setIsAddSecaoPickerOpen] = useState(false);
  const [selectedInitialContentType, setSelectedInitialContentType] = useState('texto');

  const openModuloModal = (idx) => {
    setActiveModuloIndex(idx);
    setIsModuloModalOpen(true);

    // reset do picker ao abrir modal
    setIsAddSecaoPickerOpen(false);
    setSelectedInitialContentType('texto');
  };

  const closeModuloModal = () => {
    setIsModuloModalOpen(false);
    setActiveModuloIndex(null);

    // reset do picker ao fechar modal
    setIsAddSecaoPickerOpen(false);
    setSelectedInitialContentType('texto');
  };

  useEffect(() => {
    const handleScroll = () => {
      const nearBottom =
        window.innerHeight + window.scrollY >= document.body.offsetHeight - 100;
      setScrollBottom(nearBottom);
    };
    handleScroll();
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // ESC fecha modal
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === 'Escape' && isModuloModalOpen) closeModuloModal();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isModuloModalOpen]);

  // Carrega testes globais para vincular aos módulos
  useEffect(() => {
    const carregarTestes = async () => {
      try {
        showLoader('Carregando testes');
        const resp = await listarTestes();
        if (resp.success) {
          setTestes(resp.data || []);
        } else {
          showToast('Erro ao carregar testes.', 3000, 'error');
        }
      } catch (e) {
        console.error(e);
        showToast('Erro ao carregar testes.', 3000, 'error');
      } finally {
        hideLoader();
      }
    };

    carregarTestes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** ==== Carregamento da trilha em modo de edição ==== */

  useEffect(() => {
    if (!isEditing) return;

    const carregarTrilhaCompleta = async () => {
      try {
        showLoader('Carregando trilha');

        // 1) Carrega trilha
        const respTrilha = await obterTrilhaPorId(idTrilha);
        if (!respTrilha?.success || !respTrilha.data) {
          showToast('Erro ao carregar a trilha.', 4000, 'error');
          navigate('/trilhas');
          return;
        }

        const t = respTrilha.data;

        // certificado pode vir como 1/0 ou "1"/"0"
        const rawCert = t.certificado;
        const certificadoBool =
          rawCert === 1 || rawCert === '1' || rawCert === true || rawCert === 'true';

        setTrilhaData((prev) => ({
          ...prev,
          titulo: t.titulo || '',
          descricao: t.descricao || '',
          objetivos: t.objetivos || '',
          // campo backend: "carga_horaria"
          cargaHoraria:
            t.carga_horaria !== null && t.carga_horaria !== undefined
              ? String(t.carga_horaria)
              : '',
          certificado: certificadoBool,
          templateCertificadoFile: null,
          // campo backend: "template_certificado"
          templateCertificadoUrl: t.template_certificado
            ? `${URLS.IMG_PATH}${t.template_certificado}`
            : null,
        }));

        // 2) Carrega módulos da trilha
        const respModulos = await listarModulosPorTrilha(idTrilha);
        if (!respModulos?.success) {
          showToast('Erro ao carregar módulos da trilha.', 4000, 'warning');
          setModulos([]);
          setQtdModulos(0);
          return;
        }

        const modulosBackend = (respModulos.data || []).slice().sort((a, b) => {
          const oa = a.ordem ?? 0;
          const ob = b.ordem ?? 0;
          return oa - ob;
        });

        const modulosCompletos = await Promise.all(
          modulosBackend.map(async (m, idxModulo) => {
            // === Mini teste (miniTesteEstrutura) ===
            let perguntas = [];
            const rawMini = m.miniTesteEstrutura || m.mini_teste_estrutura || null;

            if (rawMini) {
              try {
                const parsed =
                  typeof rawMini === 'string' ? JSON.parse(rawMini || '{}') : rawMini;
                const qs = parsed.questions || [];
                const existingIds = new Set();

                perguntas = qs.map((q) => {
                  const qid =
                    q.id && typeof q.id === 'string' && q.id.trim()
                      ? q.id
                      : genUniqueId(existingIds);
                  existingIds.add(qid);

                  let opcoes = [];
                  if (q.type === 'closed') {
                    const entries = Object.entries(q.options || {});
                    opcoes = entries
                      .sort(([a], [b]) => a.localeCompare(b))
                      .map(([letra, texto]) => ({ letra, texto }));
                    opcoes = reindexLetters(opcoes);
                  }

                  return {
                    id: qid,
                    enunciado: q.question || '',
                    tipo: q.type === 'open' ? 'open' : 'closed',
                    opcoes: q.type === 'open' ? [] : opcoes,
                  };
                });
              } catch (err) {
                console.warn('Erro ao parsear miniTesteEstrutura do módulo', m.id, err);
              }
            }

            // === Seções do módulo ===
            const respSecoes = await listarSecoesPorModulo(m.id);
            let secoes = [];

            if (respSecoes?.success) {
              const secoesBackend = (respSecoes.data || []).slice().sort((a, b) => {
                const oa = a.ordem ?? 0;
                const ob = b.ordem ?? 0;
                return oa - ob;
              });

              secoes = await Promise.all(
                secoesBackend.map(async (s, idxSecao) => {
                  // === Conteúdos da seção ===
                  const respConteudos = await listarConteudosPorSecao(s.id);
                  let conteudos = [];

                  if (respConteudos?.success) {
                    const conteudosBackend = (respConteudos.data || []).slice().sort((a, b) => {
                      const oa = a.ordem ?? 0;
                      const ob = b.ordem ?? 0;
                      return oa - ob;
                    });

                    conteudos = conteudosBackend.map((c, idxConteudo) => {
                      const tipo = c.tipo || 'texto';
                      const texto = c.texto || '';

                      let listaItens = [];
                      const rawLista = c.lista_itens;
                      if (tipo === 'lista' && rawLista) {
                        if (Array.isArray(rawLista)) {
                          listaItens = rawLista;
                        } else if (typeof rawLista === 'string') {
                          try {
                            const parsed = JSON.parse(rawLista);
                            if (Array.isArray(parsed)) listaItens = parsed;
                          } catch {
                            listaItens = [];
                          }
                        }
                      }

                      const imagemPath = c.url_imagem || null;
                      const imagemPreview = imagemPath ? `${URLS.IMG_PATH}${imagemPath}` : '';

                      const pdfPath = c.url_pdf || c.url_arquivo || c.pdf || null;
                      const pdfUrl = pdfPath ? `${URLS.IMG_PATH}${pdfPath}` : '';

                      const urlLink = c.url_link || '';
                      const urlVideo = c.url_video || '';

                      return {
                        tempId: genRandomId(),
                        id: c.id, // hash
                        tipo,
                        ordem: c.ordem ?? idxConteudo + 1,
                        titulo: c.titulo || '',
                        texto,
                        listaItens,
                        newListItem: '',
                        imagemFile: null,
                        imagemPreview,
                        pdfFile: null,
                        pdfUrl,
                        urlLink,
                        urlVideo,
                      };
                    });
                  }

                  return {
                    tempId: genRandomId(),
                    id: s.id, // hash
                    titulo: s.titulo || '',
                    descricao: s.descricao || '',
                    ordem: s.ordem ?? idxSecao + 1,
                    conteudos,
                  };
                })
              );
            }

            // ✅ NOVO: criterioConclusao vindo do backend
            const criterioConclusaoRaw =
              m.criterioConclusao || m.criterio_conclusao || 'secoes';
            const criterioConclusao = CRITERIO_CONCLUSAO_OPTIONS.includes(criterioConclusaoRaw)
              ? criterioConclusaoRaw
              : 'secoes';

            return {
              tempId: genRandomId(),
              id: m.id, // hash
              titulo: m.titulo || '',
              descricao: m.descricao || '',
              ordem: m.ordem ?? idxModulo + 1,
              idTeste: m.idTeste || '',
              perguntas,
              secoes,
              status: m.status || 'rascunho',

              // NOVO
              dataAbertura: m.dataAbertura || m.data_abertura || '',

              // ✅ NOVO
              criterioConclusao,
            };
          })
        );

        // === Snapshot inicial de IDs existentes ===
        const snapshotModulos = [];
        const snapshotSecoes = [];
        const snapshotConteudos = [];

        modulosCompletos.forEach((m) => {
          if (m.id) snapshotModulos.push(m.id);
          (m.secoes || []).forEach((s) => {
            if (s.id) snapshotSecoes.push(s.id);
            (s.conteudos || []).forEach((c) => {
              if (c.id) snapshotConteudos.push(c.id);
            });
          });
        });

        setInitialSnapshot({
          modulos: snapshotModulos,
          secoes: snapshotSecoes,
          conteudos: snapshotConteudos,
        });

        setModulos(modulosCompletos);
        setQtdModulos(modulosCompletos.length);
      } catch (err) {
        console.error('Erro ao carregar trilha completa: ', err);
        showToast('Erro ao carregar dados da trilha.', 4000, 'error');
        navigate('/trilhas');
      } finally {
        hideLoader();
      }
    };

    carregarTrilhaCompleta();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditing, idTrilha]);

  /** ==== MÓDULOS: criação e ajuste de quantidade ==== */

  const criarModuloVazio = (ordem) => ({
    tempId: genRandomId(), // só para React
    titulo: '',
    descricao: '',
    ordem: ordem ?? null,
    idTeste: '', // id hash de teste global (opcional)
    perguntas: [], // mini-teste interno
    secoes: [], // seções de conteúdo
    status: 'rascunho',

    // NOVO: fica fora do modal
    dataAbertura: '', // 'YYYY-MM-DD'

    // ✅ NOVO: critério de conclusão do módulo
    criterioConclusao: 'secoes',
  });

  const criarSecaoVazia = (ordem) => ({
    tempId: genRandomId(),
    titulo: '',
    descricao: '',
    ordem: ordem ?? null,
    conteudos: [],
  });

  const criarConteudoVazio = (ordem, tipoOverride) => ({
    tempId: genRandomId(),
    tipo: tipoOverride || 'texto',
    ordem: ordem ?? null,
    titulo: '',
    texto: '',
    // lista
    listaItens: [],
    newListItem: '',
    // imagem
    imagemFile: null,
    imagemPreview: '',
    // pdf
    pdfFile: null,
    pdfUrl: '',
    // link / vídeo
    urlLink: '',
    urlVideo: '',
  });

  const ajustarQuantidadeModulos = (novaQtd) => {
    const atual = modulos.length;
    if (novaQtd < 0) novaQtd = 0;

    if (novaQtd === atual) return;

    if (novaQtd > atual) {
      const novos = [];
      for (let i = atual; i < novaQtd; i++) {
        novos.push(criarModuloVazio(i + 1));
      }
      setModulos((prev) => [...prev, ...novos]);
    } else {
      // reduz
      setModulos((prev) => prev.slice(0, novaQtd));
      // se o modal estiver aberto em um índice que saiu, fecha
      if (isModuloModalOpen && activeModuloIndex !== null && activeModuloIndex >= novaQtd) {
        closeModuloModal();
      }
    }
  };

  const handleChangeQtdModulos = (e) => {
    const value = e.target.value;
    const num = Number(value);
    if (!Number.isFinite(num) || num < 0) {
      setQtdModulos(0);
      setModulos([]);
      return;
    }
    setQtdModulos(num);
    ajustarQuantidadeModulos(num);
  };

  /** ==== HANDLERS TRILHA ==== */

  const handleChangeTrilhaField = (e) => {
    const { name, value } = e.target;
    setTrilhaData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleChangeCertificado = (e) => {
    const { value } = e.target; // "true" | "false"
    setTrilhaData((prev) => ({
      ...prev,
      certificado: value === 'true',
    }));
  };

  const handleTemplateCertificadoFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.type !== 'application/pdf') {
      showToast('Por favor, selecione um arquivo PDF válido para o certificado.', 3000, 'warning');
      return;
    }

    setTrilhaData((prev) => ({
      ...prev,
      templateCertificadoFile: file,
      templateCertificadoUrl: prev.templateCertificadoUrl,
    }));
  };

  /** ==== HANDLERS MÓDULO (campos básicos) ==== */

  const handleChangeModuloField = (index, field, value) => {
    setModulos((prev) => {
      const arr = [...prev];
      const mod = { ...arr[index] };

      if (field === 'ordem') {
        const n = Number(value);
        mod.ordem = Number.isFinite(n) ? n : null;
      } else if (field === 'criterioConclusao') {
        const v = String(value || '').trim();
        mod.criterioConclusao = CRITERIO_CONCLUSAO_OPTIONS.includes(v) ? v : 'secoes';
      } else {
        mod[field] = value;
      }

      arr[index] = mod;
      return arr;
    });
  };

  /** ==== SEÇÕES ==== */

  // (NOVO) adiciona seção já com 1 conteúdo do tipo selecionado
  const handleAddSecaoWithInitialContent = (idxModulo, initialContentType) => {
    const tipoEscolhido = CONTENT_TYPES.includes(initialContentType) ? initialContentType : 'texto';

    setModulos((prev) => {
      const arr = [...prev];
      const mod = { ...arr[idxModulo] };

      const ordemSecao = (mod.secoes?.length || 0) + 1;
      const novaSecao = criarSecaoVazia(ordemSecao);

      // já começa com 1 conteúdo do tipo selecionado
      novaSecao.conteudos = [criarConteudoVazio(1, tipoEscolhido)];

      mod.secoes = [...(mod.secoes || []), novaSecao];
      arr[idxModulo] = mod;
      return arr;
    });

    showToast(
      `Seção adicionada (iniciando com "${CONTENT_TYPE_LABEL[tipoEscolhido]}").`,
      2500,
      'success'
    );
  };

  // (mantido) adiciona seção vazia (ainda usado em alguns lugares)
  const handleAddSecao = (idxModulo) => {
    setModulos((prev) => {
      const arr = [...prev];
      const mod = { ...arr[idxModulo] };
      const ordem = (mod.secoes?.length || 0) + 1;
      mod.secoes = [...(mod.secoes || []), criarSecaoVazia(ordem)];
      arr[idxModulo] = mod;
      return arr;
    });

    showToast('Seção adicionada.', 2500, 'success');
  };

  const handleRemoveSecao = (idxModulo, idxSecao) => {
    setModulos((prev) => {
      const arr = [...prev];
      const mod = { ...arr[idxModulo] };
      mod.secoes = (mod.secoes || []).filter((_, i) => i !== idxSecao);
      arr[idxModulo] = mod;
      return arr;
    });
  };

  const handleChangeSecaoField = (idxModulo, idxSecao, field, value) => {
    setModulos((prev) => {
      const arr = [...prev];
      const mod = { ...arr[idxModulo] };
      const secoes = [...(mod.secoes || [])];
      const sec = { ...secoes[idxSecao] };

      if (field === 'ordem') {
        const n = Number(value);
        sec.ordem = Number.isFinite(n) ? n : null;
      } else {
        sec[field] = value;
      }

      secoes[idxSecao] = sec;
      mod.secoes = secoes;
      arr[idxModulo] = mod;
      return arr;
    });
  };

  /** ==== CONTEÚDOS ==== */

  const handleAddConteudo = (idxModulo, idxSecao) => {
    setModulos((prev) => {
      const arr = [...prev];
      const mod = { ...arr[idxModulo] };
      const secoes = [...(mod.secoes || [])];
      const sec = { ...secoes[idxSecao] };

      const ordem = (sec.conteudos?.length || 0) + 1;
      sec.conteudos = [...(sec.conteudos || []), criarConteudoVazio(ordem)];

      secoes[idxSecao] = sec;
      mod.secoes = secoes;
      arr[idxModulo] = mod;
      return arr;
    });
  };

  const handleRemoveConteudo = (idxModulo, idxSecao, idxConteudo) => {
    setModulos((prev) => {
      const arr = [...prev];
      const mod = { ...arr[idxModulo] };
      const secoes = [...(mod.secoes || [])];
      const sec = { ...secoes[idxSecao] };

      sec.conteudos = (sec.conteudos || []).filter((_, i) => i !== idxConteudo);

      secoes[idxSecao] = sec;
      mod.secoes = secoes;
      arr[idxModulo] = mod;
      return arr;
    });
  };

  const handleChangeConteudoField = (idxModulo, idxSecao, idxConteudo, field, value) => {
    setModulos((prev) => {
      const arr = [...prev];
      const mod = { ...arr[idxModulo] };
      const secoes = [...(mod.secoes || [])];
      const sec = { ...secoes[idxSecao] };
      const conteudos = [...(sec.conteudos || [])];
      const cont = { ...conteudos[idxConteudo] };

      if (field === 'tipo') {
        cont.tipo = value;

        if (value !== 'lista') {
          cont.listaItens = [];
          cont.newListItem = '';
        }
        if (value !== 'imagem') {
          cont.imagemFile = null;
          cont.imagemPreview = '';
        }
        if (value !== 'pdf') {
          cont.pdfFile = null;
          cont.pdfUrl = cont.pdfUrl || '';
        }
        if (value !== 'link') {
          cont.urlLink = '';
        }
        if (value !== 'video') {
          cont.urlVideo = '';
        }
        if (value !== 'texto') {
          cont.texto = '';
        }
      } else if (field === 'ordem') {
        const n = Number(value);
        cont.ordem = Number.isFinite(n) ? n : null;
      } else {
        cont[field] = value;
      }

      conteudos[idxConteudo] = cont;
      sec.conteudos = conteudos;
      secoes[idxSecao] = sec;
      mod.secoes = secoes;
      arr[idxModulo] = mod;
      return arr;
    });
  };

  /** ==== LISTA (conteúdo tipo "lista") ==== */

  const handleAddListItem = (idxModulo, idxSecao, idxConteudo) => {
    setModulos((prev) => {
      const arr = [...prev];
      const mod = { ...arr[idxModulo] };
      const secoes = [...(mod.secoes || [])];
      const sec = { ...secoes[idxSecao] };
      const conteudos = [...(sec.conteudos || [])];
      const cont = { ...conteudos[idxConteudo] };

      const texto = (cont.newListItem || '').trim();
      if (!texto) return prev;

      cont.listaItens = [...(cont.listaItens || []), texto];
      cont.newListItem = '';

      conteudos[idxConteudo] = cont;
      sec.conteudos = conteudos;
      secoes[idxSecao] = sec;
      mod.secoes = secoes;
      arr[idxModulo] = mod;
      return arr;
    });
  };

  const handleRemoveListItem = (idxModulo, idxSecao, idxConteudo, idxItem) => {
    setModulos((prev) => {
      const arr = [...prev];
      const mod = { ...arr[idxModulo] };
      const secoes = [...(mod.secoes || [])];
      const sec = { ...secoes[idxSecao] };
      const conteudos = [...(sec.conteudos || [])];
      const cont = { ...conteudos[idxConteudo] };

      cont.listaItens = (cont.listaItens || []).filter((_, i) => i !== idxItem);

      conteudos[idxConteudo] = cont;
      sec.conteudos = conteudos;
      secoes[idxSecao] = sec;
      mod.secoes = secoes;
      arr[idxModulo] = mod;
      return arr;
    });
  };

  /** ==== ARQUIVOS (IMAGEM / PDF) ==== */

  const handleImagemFileChange = (idxModulo, idxSecao, idxConteudo, e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      showToast('Selecione um arquivo de imagem válido.', 3000, 'warning');
      return;
    }

    const preview = URL.createObjectURL(file);

    setModulos((prev) => {
      const arr = [...prev];
      const mod = { ...arr[idxModulo] };
      const secoes = [...(mod.secoes || [])];
      const sec = { ...secoes[idxSecao] };
      const conteudos = [...(sec.conteudos || [])];
      const cont = { ...conteudos[idxConteudo] };

      cont.imagemFile = file;
      cont.imagemPreview = preview;

      conteudos[idxConteudo] = cont;
      sec.conteudos = conteudos;
      secoes[idxSecao] = sec;
      mod.secoes = secoes;
      arr[idxModulo] = mod;
      return arr;
    });
  };

  const handlePdfFileChange = (idxModulo, idxSecao, idxConteudo, e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.type !== 'application/pdf') {
      showToast('Selecione um arquivo PDF válido.', 3000, 'warning');
      return;
    }

    setModulos((prev) => {
      const arr = [...prev];
      const mod = { ...arr[idxModulo] };
      const secoes = [...(mod.secoes || [])];
      const sec = { ...secoes[idxSecao] };
      const conteudos = [...(sec.conteudos || [])];
      const cont = { ...conteudos[idxConteudo] };

      cont.pdfFile = file;
      conteudos[idxConteudo] = cont;
      sec.conteudos = conteudos;
      secoes[idxSecao] = sec;
      mod.secoes = secoes;
      arr[idxModulo] = mod;
      return arr;
    });
  };

  /** ==== MINI TESTE POR MÓDULO ==== */

  const currentQuestionIdsModulo = (mod) =>
    new Set((mod.perguntas || []).map((p) => p.id).filter(Boolean));

  const handleAddPerguntaModulo = (idxModulo) => {
    setModulos((prev) => {
      const arr = [...prev];
      const mod = { ...arr[idxModulo] };
      const usedIds = currentQuestionIdsModulo(mod);

      const newId = genUniqueId(usedIds);

      const novaPergunta = {
        id: newId,
        enunciado: '',
        tipo: 'closed',
        opcoes: [
          { letra: 'A', texto: '' },
          { letra: 'B', texto: '' },
        ],
      };

      mod.perguntas = [...(mod.perguntas || []), novaPergunta];
      arr[idxModulo] = mod;
      return arr;
    });

    showToast('Pergunta adicionada ao mini teste.', 2500, 'success');
  };

  const handleRemovePerguntaModulo = (idxModulo, idxPergunta) => {
    setModulos((prev) => {
      const arr = [...prev];
      const mod = { ...arr[idxModulo] };
      mod.perguntas = (mod.perguntas || []).filter((_, i) => i !== idxPergunta);
      arr[idxModulo] = mod;
      return arr;
    });
  };

  const handleChangePerguntaModulo = (idxModulo, idxPergunta, field, value) => {
    setModulos((prev) => {
      const arr = [...prev];
      const mod = { ...arr[idxModulo] };
      const perguntas = [...(mod.perguntas || [])];
      const atual = { ...perguntas[idxPergunta] };

      if (!atual.id || typeof atual.id !== 'string' || !atual.id.trim()) {
        const used = currentQuestionIdsModulo(mod);
        atual.id = genUniqueId(used);
      }

      if (field === 'tipo') {
        atual.tipo = value;

        if (value === 'open') {
          atual.opcoes = [];
        } else if (value === 'closed') {
          if (!atual.opcoes || atual.opcoes.length < 2) {
            atual.opcoes = [
              { letra: 'A', texto: '' },
              { letra: 'B', texto: '' },
            ];
          } else {
            atual.opcoes = reindexLetters(atual.opcoes);
          }
        }
      } else {
        atual[field] = value;
      }

      perguntas[idxPergunta] = atual;
      mod.perguntas = perguntas;
      arr[idxModulo] = mod;
      return arr;
    });
  };

  const handleChangeOpcaoModulo = (idxModulo, idxPergunta, idxOpcao, campo, valor) => {
    setModulos((prev) => {
      const arr = [...prev];
      const mod = { ...arr[idxModulo] };
      const perguntas = [...(mod.perguntas || [])];
      const p = { ...perguntas[idxPergunta] };

      if (!p.id || typeof p.id !== 'string' || !p.id.trim()) {
        const used = currentQuestionIdsModulo(mod);
        p.id = genUniqueId(used);
      }

      p.opcoes = [...(p.opcoes || [])];
      p.opcoes[idxOpcao] = { ...p.opcoes[idxOpcao], [campo]: valor };
      p.opcoes = reindexLetters(p.opcoes);

      perguntas[idxPergunta] = p;
      mod.perguntas = perguntas;
      arr[idxModulo] = mod;
      return arr;
    });
  };

  const handleAddOpcaoModulo = (idxModulo, idxPergunta) => {
    setModulos((prev) => {
      const arr = [...prev];
      const mod = { ...arr[idxModulo] };
      const perguntas = [...(mod.perguntas || [])];
      const p = { ...perguntas[idxPergunta] };

      p.opcoes = [...(p.opcoes || [])];

      if (p.opcoes.length >= 26) {
        showToast('Limite de 26 opções atingido (A-Z)', 3000, 'warning');
        return prev;
      }

      p.opcoes = reindexLetters(p.opcoes);
      const L = nextAvailableLetter(p.opcoes) ?? String.fromCharCode(65 + p.opcoes.length);
      p.opcoes.push({ letra: L, texto: '' });
      p.opcoes = reindexLetters(p.opcoes);

      perguntas[idxPergunta] = p;
      mod.perguntas = perguntas;
      arr[idxModulo] = mod;
      return arr;
    });
  };

  /** ==== SUBMIT (criação + edição) ==== */

  const handleSubmit = async (e) => {
    e.preventDefault();

    const titulo = (trilhaData.titulo || '').trim();
    const descricao = (trilhaData.descricao || '').trim();

    if (!titulo || !descricao) {
      showToast('Preencha ao menos título e descrição da trilha.', 3000, 'warning');
      return;
    }

    if (trilhaData.certificado && !trilhaData.templateCertificadoFile && !trilhaData.templateCertificadoUrl) {
      showToast(
        'Você marcou que a trilha emite certificado. Selecione o PDF do template.',
        3500,
        'warning'
      );
      return;
    }

    try {
      showLoader(isEditing ? 'Atualizando trilha' : 'Criando trilha');

      // IDs para publicação no final (sempre publica tudo ao finalizar)
      const moduloIdsParaPublicar = new Set();
      const secaoIdsParaPublicar = new Set();

      // 1) FormData da trilha (texto + arquivo PDF)
      const trilhaFormData = new FormData();
      trilhaFormData.append('titulo', titulo);
      trilhaFormData.append('descricao', descricao);
      trilhaFormData.append('objetivos', trilhaData.objetivos || '');
      trilhaFormData.append('cargaHoraria', trilhaData.cargaHoraria || '');
      trilhaFormData.append('certificado', trilhaData.certificado ? 1 : 0);

      if (isEditing) {
        trilhaFormData.append('id', idTrilha);
      }

      if (trilhaData.templateCertificadoFile) {
        trilhaFormData.append('templateCertificado', trilhaData.templateCertificadoFile);
      }

      // 2) Cria ou atualiza trilha via API
      const respTrilha = isEditing ? await atualizarTrilha(trilhaFormData) : await criarTrilha(trilhaFormData);

      if (!respTrilha?.success) {
        console.log('Falha ao salvar trilha: ', respTrilha);
        showToast(respTrilha.message || 'Erro ao salvar trilha.', 4000, 'error');
        hideLoader();
        return;
      }

      const trilhaIdHash = isEditing ? idTrilha : respTrilha.id;

      if (!isEditing) {
        // === NOVA TRILHA: criação ===
        for (let i = 0; i < modulos.length; i++) {
          const mod = modulos[i];

          const perguntasComId = (mod.perguntas || []).map((pOriginal, idx, arr) => {
            const p = { ...pOriginal };
            if (!p.id || typeof p.id !== 'string' || !p.id.trim()) {
              const used = new Set(arr.map((px) => px.id).filter(Boolean));
              p.id = genUniqueId(used);
            }
            return p;
          });

          const questions = perguntasComId.map((p) => {
            const en = (p.enunciado || '').trim();

            if (p.tipo === 'open') {
              return { id: p.id, question: en, type: 'open' };
            }

            const opcoesNorm = reindexLetters(p.opcoes || []);
            const optionsObj = Object.fromEntries(
              opcoesNorm.map((o) => [o.letra, (o.texto || '').trim()])
            );

            return { id: p.id, question: en, type: 'closed', options: optionsObj };
          });

          const miniTesteEstrutura = questions.length > 0 ? JSON.stringify({ questions }) : null;

          const criterioConclusao =
            CRITERIO_CONCLUSAO_OPTIONS.includes(mod.criterioConclusao)
              ? mod.criterioConclusao
              : 'secoes';

          const payloadModulo = {
            idTrilha: trilhaIdHash,
            titulo: (mod.titulo || '').trim() || `Módulo ${i + 1}`,
            descricao: mod.descricao || '',
            ordem: mod.ordem ?? i + 1,
            idTeste: mod.idTeste || null,
            miniTesteEstrutura,
            status: 'rascunho',
            dataAbertura: mod.dataAbertura || null,

            // ✅ NOVO: envia para o endpoint
            criterioConclusao,
          };

          const respModulo = await criarModulo(payloadModulo);

          if (!respModulo?.success) {
            showToast(
              `A trilha foi criada, mas houve erro ao criar o módulo ${i + 1}.`,
              5000,
              'warning'
            );
            continue;
          }

          const idModuloHash = respModulo.id;
          if (idModuloHash) moduloIdsParaPublicar.add(idModuloHash);

          // Seções
          const secoesDoModulo = mod.secoes || [];
          for (let j = 0; j < secoesDoModulo.length; j++) {
            const sec = secoesDoModulo[j];

            const payloadSecao = {
              idModulo: idModuloHash,
              titulo: (sec.titulo || '').trim() || `Seção ${j + 1}`,
              descricao: sec.descricao || '',
              ordem: sec.ordem ?? j + 1,
            };

            const respSecao = await criarSecao(payloadSecao);

            if (!respSecao?.success) {
              showToast(
                `Módulo ${i + 1} criado, mas houve erro ao criar a seção ${j + 1}.`,
                4000,
                'warning'
              );
              continue;
            }

            const idSecaoHash = respSecao.id;
            if (idSecaoHash) secaoIdsParaPublicar.add(idSecaoHash);

            // Conteúdos
            const conteudosDaSecao = sec.conteudos || [];
            for (let k = 0; k < conteudosDaSecao.length; k++) {
              const cont = conteudosDaSecao[k];
              const tipo = cont.tipo || 'texto';

              const conteudoFormData = new FormData();
              conteudoFormData.append('idSecao', idSecaoHash);
              conteudoFormData.append('tipo', tipo);
              conteudoFormData.append('ordem', cont.ordem ?? k + 1);

              if (cont.titulo) conteudoFormData.append('titulo', cont.titulo);

              if (tipo === 'texto' && cont.texto) conteudoFormData.append('texto', cont.texto);

              if (tipo === 'lista') {
                const itens = cont.listaItens || [];
                if (itens.length > 0) conteudoFormData.append('listaItens', JSON.stringify(itens));
              }

              if (tipo === 'imagem') {
                if (cont.imagemFile) conteudoFormData.append('imagem', cont.imagemFile);
              }

              if (tipo === 'pdf') {
                if (cont.pdfFile) conteudoFormData.append('pdf', cont.pdfFile);
              }

              if (tipo === 'link' && cont.urlLink) conteudoFormData.append('urlLink', cont.urlLink);
              if (tipo === 'video' && cont.urlVideo) conteudoFormData.append('urlVideo', cont.urlVideo);

              const respConteudo = await criarConteudo(conteudoFormData);

              if (!respConteudo?.success) {
                showToast(
                  `Seção ${j + 1} criada, mas houve erro ao criar o conteúdo ${k + 1}.`,
                  4000,
                  'warning'
                );
              }
            }
          }
        }
      } else {
        // === EDIÇÃO COMPLETA ===

        const currentModuloIds = new Set(modulos.filter((m) => m.id).map((m) => m.id));
        const currentSecaoIds = new Set(
          modulos.flatMap((m) => m.secoes || []).filter((s) => s.id).map((s) => s.id)
        );
        const currentConteudoIds = new Set(
          modulos
            .flatMap((m) => m.secoes || [])
            .flatMap((s) => s.conteudos || [])
            .filter((c) => c.id)
            .map((c) => c.id)
        );

        const deletedModuloIds = (initialSnapshot.modulos || []).filter((id) => !currentModuloIds.has(id));
        const deletedSecaoIds = (initialSnapshot.secoes || []).filter((id) => !currentSecaoIds.has(id));
        const deletedConteudoIds = (initialSnapshot.conteudos || []).filter((id) => !currentConteudoIds.has(id));

        for (let i = 0; i < modulos.length; i++) {
          const mod = modulos[i];

          const perguntasComId = (mod.perguntas || []).map((pOriginal, idx, arr) => {
            const p = { ...pOriginal };
            if (!p.id || typeof p.id !== 'string' || !p.id.trim()) {
              const used = new Set(arr.map((px) => px.id).filter(Boolean));
              p.id = genUniqueId(used);
            }
            return p;
          });

          const questions = perguntasComId.map((p) => {
            const en = (p.enunciado || '').trim();

            if (p.tipo === 'open') return { id: p.id, question: en, type: 'open' };

            const opcoesNorm = reindexLetters(p.opcoes || []);
            const optionsObj = Object.fromEntries(
              opcoesNorm.map((o) => [o.letra, (o.texto || '').trim()])
            );
            return { id: p.id, question: en, type: 'closed', options: optionsObj };
          });

          const miniTesteEstrutura = questions.length > 0 ? JSON.stringify({ questions }) : null;

          const criterioConclusao =
            CRITERIO_CONCLUSAO_OPTIONS.includes(mod.criterioConclusao)
              ? mod.criterioConclusao
              : 'secoes';

          let idModuloHash = mod.id || null;

          if (mod.id) {
            const payloadModulo = {
              id: mod.id,
              idTrilha: trilhaIdHash,
              titulo: (mod.titulo || '').trim() || `Módulo ${i + 1}`,
              descricao: mod.descricao || '',
              ordem: mod.ordem ?? i + 1,
              idTeste: mod.idTeste || null,
              miniTesteEstrutura,
              status: mod.status || 'rascunho',
              dataAbertura: mod.dataAbertura || null,

              // ✅ NOVO: envia para o endpoint
              criterioConclusao,
            };

            const respModulo = await atualizarModulo(payloadModulo);

            if (!respModulo?.success) {
              showToast(
                `Houve erro ao atualizar o módulo ${i + 1}. Verifique os dados.`,
                5000,
                'warning'
              );
              continue;
            }
          } else {
            const payloadModulo = {
              idTrilha: trilhaIdHash,
              titulo: (mod.titulo || '').trim() || `Módulo ${i + 1}`,
              descricao: mod.descricao || '',
              ordem: mod.ordem ?? i + 1,
              idTeste: mod.idTeste || null,
              miniTesteEstrutura,
              status: 'rascunho',
              dataAbertura: mod.dataAbertura || null,

              // ✅ NOVO
              criterioConclusao,
            };

            const respModulo = await criarModulo(payloadModulo);

            if (!respModulo?.success) {
              showToast(`Houve erro ao criar o módulo ${i + 1} durante a edição.`, 5000, 'warning');
              continue;
            }

            idModuloHash = respModulo.id;

            // atualiza state local com o hash novo (pra não perder referência)
            setModulos((prev) => {
              const arr = [...prev];
              arr[i] = { ...arr[i], id: idModuloHash };
              return arr;
            });
          }

          if (!idModuloHash) continue;
          moduloIdsParaPublicar.add(idModuloHash);

          // Seções (atualizar/criar)
          const secoesDoModulo = mod.secoes || [];
          for (let j = 0; j < secoesDoModulo.length; j++) {
            const sec = secoesDoModulo[j];
            let idSecaoHash = sec.id || null;

            if (sec.id) {
              const payloadSecao = {
                id: sec.id,
                idModulo: idModuloHash,
                titulo: (sec.titulo || '').trim() || `Seção ${j + 1}`,
                descricao: sec.descricao || '',
                ordem: sec.ordem ?? j + 1,
              };

              const respSecao = await atualizarSecao(payloadSecao);

              if (!respSecao?.success) {
                showToast(
                  `Erro ao atualizar a seção ${j + 1} do módulo ${i + 1}.`,
                  4000,
                  'warning'
                );
                continue;
              }
            } else {
              const payloadSecao = {
                idModulo: idModuloHash,
                titulo: (sec.titulo || '').trim() || `Seção ${j + 1}`,
                descricao: sec.descricao || '',
                ordem: sec.ordem ?? j + 1,
              };

              const respSecao = await criarSecao(payloadSecao);

              if (!respSecao?.success) {
                showToast(
                  `Módulo ${i + 1} salvo, mas houve erro ao criar a seção ${j + 1}.`,
                  4000,
                  'warning'
                );
                continue;
              }

              idSecaoHash = respSecao.id;

              // atualiza state local com o hash novo
              setModulos((prev) => {
                const arr = [...prev];
                const modLocal = { ...arr[i] };
                const secoesLocal = [...(modLocal.secoes || [])];
                secoesLocal[j] = { ...secoesLocal[j], id: idSecaoHash };
                modLocal.secoes = secoesLocal;
                arr[i] = modLocal;
                return arr;
              });
            }

            if (!idSecaoHash) continue;
            secaoIdsParaPublicar.add(idSecaoHash);

            // Conteúdos (atualizar/criar)
            const conteudosDaSecao = sec.conteudos || [];
            for (let k = 0; k < conteudosDaSecao.length; k++) {
              const cont = conteudosDaSecao[k];
              const tipo = cont.tipo || 'texto';

              if (cont.id) {
                const precisaUploadImagem = tipo === 'imagem' && cont.imagemFile;
                const precisaUploadPdf = tipo === 'pdf' && cont.pdfFile;

                let respConteudo;

                if (precisaUploadImagem || precisaUploadPdf) {
                  const conteudoFormData = new FormData();
                  conteudoFormData.append('id', cont.id);
                  conteudoFormData.append('idSecao', idSecaoHash);
                  conteudoFormData.append('tipo', tipo);
                  conteudoFormData.append('ordem', cont.ordem ?? k + 1);

                  if (cont.titulo) conteudoFormData.append('titulo', cont.titulo);
                  if (tipo === 'texto' && cont.texto) conteudoFormData.append('texto', cont.texto);

                  if (tipo === 'lista') {
                    const itens = cont.listaItens || [];
                    conteudoFormData.append('listaItens', JSON.stringify(itens));
                  }

                  if (tipo === 'imagem' && cont.imagemFile) conteudoFormData.append('imagem', cont.imagemFile);
                  if (tipo === 'pdf' && cont.pdfFile) conteudoFormData.append('pdf', cont.pdfFile);

                  if (tipo === 'link' && cont.urlLink) conteudoFormData.append('urlLink', cont.urlLink);
                  if (tipo === 'video' && cont.urlVideo) conteudoFormData.append('urlVideo', cont.urlVideo);

                  respConteudo = await atualizarConteudo(conteudoFormData);
                } else {
                  const payloadConteudo = {
                    id: cont.id,
                    idSecao: idSecaoHash,
                    tipo,
                    ordem: cont.ordem ?? k + 1,
                    titulo: cont.titulo || '',
                  };

                  if (tipo === 'texto') payloadConteudo.texto = cont.texto || '';
                  if (tipo === 'lista') payloadConteudo.listaItens = cont.listaItens || [];
                  if (tipo === 'link') payloadConteudo.urlLink = cont.urlLink || '';
                  if (tipo === 'video') payloadConteudo.urlVideo = cont.urlVideo || '';

                  respConteudo = await atualizarConteudo(payloadConteudo);
                }

                if (!respConteudo?.success) {
                  showToast(
                    `Erro ao atualizar o conteúdo ${k + 1} da seção ${j + 1} do módulo ${i + 1}.`,
                    4000,
                    'warning'
                  );
                }
              } else {
                const conteudoFormData = new FormData();
                conteudoFormData.append('idSecao', idSecaoHash);
                conteudoFormData.append('tipo', tipo);
                conteudoFormData.append('ordem', cont.ordem ?? k + 1);

                if (cont.titulo) conteudoFormData.append('titulo', cont.titulo);
                if (tipo === 'texto' && cont.texto) conteudoFormData.append('texto', cont.texto);

                if (tipo === 'lista') {
                  const itens = cont.listaItens || [];
                  if (itens.length > 0) conteudoFormData.append('listaItens', JSON.stringify(itens));
                }

                if (tipo === 'imagem' && cont.imagemFile) conteudoFormData.append('imagem', cont.imagemFile);
                if (tipo === 'pdf' && cont.pdfFile) conteudoFormData.append('pdf', cont.pdfFile);

                if (tipo === 'link' && cont.urlLink) conteudoFormData.append('urlLink', cont.urlLink);
                if (tipo === 'video' && cont.urlVideo) conteudoFormData.append('urlVideo', cont.urlVideo);

                const respConteudo = await criarConteudo(conteudoFormData);

                if (!respConteudo?.success) {
                  showToast(
                    `Seção ${j + 1} salva, mas houve erro ao criar o conteúdo ${k + 1}.`,
                    4000,
                    'warning'
                  );
                }
              }
            }
          }
        }

        // Deleta removidos (conteúdos -> seções -> módulos)
        for (const contId of deletedConteudoIds) {
          try {
            const respDelCont = await deletarConteudo(contId);
            if (!respDelCont?.success) {
              showToast('Não foi possível excluir um conteúdo removido na tela.', 4000, 'warning');
            }
          } catch (err) {
            console.error('Erro ao deletar conteúdo', contId, err);
            showToast('Erro ao excluir um conteúdo removido na tela.', 4000, 'warning');
          }
        }

        for (const secId of deletedSecaoIds) {
          try {
            const respDelSec = await deletarSecao(secId);
            if (!respDelSec?.success) {
              showToast('Não foi possível excluir uma seção removida na tela.', 4000, 'warning');
            }
          } catch (err) {
            console.error('Erro ao deletar seção', secId, err);
            showToast('Erro ao excluir uma seção removida na tela.', 4000, 'warning');
          }
        }

        for (const modId of deletedModuloIds) {
          try {
            const respDelMod = await deletarModulo(modId);
            if (!respDelMod?.success) {
              showToast('Não foi possível excluir um módulo removido na tela.', 4000, 'warning');
            }
          } catch (err) {
            console.error('Erro ao deletar módulo', modId, err);
            showToast('Erro ao excluir um módulo removido na tela.', 4000, 'warning');
          }
        }
      }

      // ======================
      // PUBLICAÇÃO AUTOMÁTICA (SEMPRE ao finalizar salvar/editar)
      // Ordem recomendada: publica seções primeiro e depois módulos.
      // ======================
      // Remove qualquer seção que foi deletada na edição (caso ainda estivesse em snapshot)
      const deletedSecaoSet = new Set(
        isEditing
          ? (initialSnapshot.secoes || []).filter(
              (id) =>
                !new Set(
                  modulos
                    .flatMap((m) => m.secoes || [])
                    .filter((s) => s.id)
                    .map((s) => s.id)
                ).has(id)
            )
          : []
      );

      for (const secId of Array.from(secaoIdsParaPublicar)) {
        if (deletedSecaoSet.has(secId)) continue;
        try {
          const respPubSec = await publicarSecao(secId);
          if (!respPubSec?.success) {
            console.warn('Falha ao publicar seção', secId, respPubSec);
            // não interrompe
          }
        } catch (err) {
          console.error('Erro ao publicar seção', secId, err);
        }
      }

      const deletedModuloSet = new Set(
        isEditing
          ? (initialSnapshot.modulos || []).filter(
              (id) =>
                !new Set(modulos.filter((m) => m.id).map((m) => m.id)).has(id)
            )
          : []
      );

      for (const modId of Array.from(moduloIdsParaPublicar)) {
        if (deletedModuloSet.has(modId)) continue;
        try {
          const respPubMod = await publicarModulo(modId);
          if (!respPubMod?.success) {
            console.warn('Falha ao publicar módulo', modId, respPubMod);
            // não interrompe
          }
        } catch (err) {
          console.error('Erro ao publicar módulo', modId, err);
        }
      }

      showToast(isEditing ? 'Trilha atualizada com sucesso!' : 'Trilha criada com sucesso!', 3000, 'success');
      hideLoader();
      navigate('/trilhas');
    } catch (err) {
      console.error('Erro ao salvar trilha/módulos/seções/conteúdos: ', err);
      showToast('Erro ao salvar trilha.', 4000, 'error');
      hideLoader();
    }
  };

  /** ==== RENDER ==== */

  const breadcrumbLabel = isEditing ? `${trilhaData.titulo || 'trilha'}` : undefined;

  const activeModulo =
    activeModuloIndex !== null && activeModuloIndex !== undefined ? modulos[activeModuloIndex] : null;

  return (
    <div className={styles.TrilhaFormPage}>
      <div className={styles.containerTop}>
        <Breadcrumbs idLabels={breadcrumbLabel ? [{ id: idTrilha, label: breadcrumbLabel }] : []} />
        <h1 className={styles.title}>{isEditing ? 'Editar Trilha' : 'Cadastro de Trilha'}</h1>
        <FriendlyPhrase page={isEditing ? 'atualizarTrilha' : 'adicionarTrilha'} />
      </div>

      <form onSubmit={handleSubmit} className={styles.formulario}>
        {/* ==== DADOS DA TRILHA ==== */}
        <div className={styles.generalArea}>
          <h2 className={styles.sectionTitle}>Dados da Trilha</h2>

          <label>
            Título:
            <input type="text" name="titulo" value={trilhaData.titulo} onChange={handleChangeTrilhaField} required />
          </label>

          <label>
            Descrição:
            <textarea name="descricao" value={trilhaData.descricao} onChange={handleChangeTrilhaField} required />
          </label>

          <label>
            Objetivos:
            <textarea
              name="objetivos"
              value={trilhaData.objetivos}
              onChange={handleChangeTrilhaField}
              placeholder="Quais são os objetivos dessa trilha?"
            />
          </label>

          <label>
            Carga horária:
            <input
              type="text"
              name="cargaHoraria"
              value={trilhaData.cargaHoraria}
              onChange={handleChangeTrilhaField}
              placeholder="Ex.: 8h, 12h, 20h..."
            />
          </label>

          <label>
            Emitirá certificado ao final?
            <select
              name="certificado"
              value={trilhaData.certificado}
              onChange={handleChangeCertificado}
              className={styles.selectInput}
              required
            >
              <option value="" disabled>
                Selecione uma opção
              </option>
              <option value={true}>Sim</option>
              <option value={false}>Não</option>
            </select>
          </label>

          {trilhaData.certificado && (
            <label>
              Template de certificado (PDF):
              <div className={styles.fileBlock}>
                <div className={styles.fileInputWrapper}>
                  <input
                    id="templateCertificadoFile"
                    type="file"
                    accept="application/pdf"
                    className={styles.fileInput}
                    onChange={handleTemplateCertificadoFileChange}
                  />
                  <label htmlFor="templateCertificadoFile" className={`${styles.fileLabel} ${styles.fileLabelGhost}`}>
                    Selecionar arquivo PDF
                  </label>
                </div>

                {(trilhaData.templateCertificadoFile || trilhaData.templateCertificadoUrl) && (
                  <div className={styles.filePreview}>
                    <div className={styles.pdfIcon}>PDF</div>
                    <div className={styles.filePreviewInfo}>
                      <span className={styles.filePreviewLabel}>
                        {trilhaData.templateCertificadoFile ? 'Arquivo selecionado' : 'Arquivo atual'}
                      </span>
                      <span className={styles.filePreviewName}>
                        {trilhaData.templateCertificadoFile
                          ? trilhaData.templateCertificadoFile.name
                          : (trilhaData.templateCertificadoUrl || '').split('/').pop()}
                      </span>
                      {trilhaData.templateCertificadoUrl && (
                        <a
                          href={trilhaData.templateCertificadoUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={styles.filePreviewLink}
                        >
                          Abrir PDF
                        </a>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </label>
          )}
        </div>

        {/* ==== MÓDULOS ==== */}
        <div className={styles.modulesArea}>
          <h2 className={styles.sectionTitle}>Módulos da Trilha</h2>

          <label>
            Quantidade de módulos:
            <input
              type="number"
              min={0}
              value={qtdModulos}
              onChange={handleChangeQtdModulos}
              className={styles.numberInput}
            />
          </label>

          {modulos.length > 0 && <p className={styles.mutedNote}>Clique na caixa do módulo para editar o conteúdo.</p>}

          {modulos.length > 0 && (
            <div className={styles.modulesGrid}>
              {modulos.map((mod, idxModulo) => (
                <div key={mod.tempId} className={styles.moduleTile}>
                  <button
                    type="button"
                    className={styles.moduleTileMainBtn}
                    onClick={() => openModuloModal(idxModulo)}
                    title="Clique para editar este módulo"
                  >
                    <div className={styles.moduleTileHeader}>
                      <div className={styles.moduleTileTitle}>
                        <span className={styles.moduleTileKicker}>Módulo {idxModulo + 1}</span>
                        <div className={styles.moduleTileName}>
                          {(mod.titulo || '').trim() ? mod.titulo : 'Título do módulo (editar)'}
                        </div>
                      </div>

                      <span className={styles.moduleTileIcon}>
                        <FiEdit3 />
                      </span>
                    </div>

                    <div className={styles.moduleTileHint}>
                      {(mod.descricao || '').trim() ? mod.descricao : 'Adicionar descrição (opcional)'}
                    </div>

                    <div className={styles.moduleTileFooterPills}>
                      <span className={`${styles.pill} ${styles.pillOk}`}>Status: {mod.status || 'rascunho'}</span>
                      <span className={styles.pill}>Ordem: {mod.ordem ?? idxModulo + 1}</span>
                    </div>
                  </button>

                  <div className={styles.moduleDateBlock}>
                    <span className={styles.moduleDateLabel}>Data de abertura</span>
                    <input
                      type="date"
                      value={mod.dataAbertura || ''}
                      onChange={(e) => handleChangeModuloField(idxModulo, 'dataAbertura', e.target.value)}
                      className={`${styles.dateInput} ${styles.moduleDateInput}`}
                    />
                  </div>

                  {/* ✅ NOVO: critério de conclusão (fora do modal, igual a data) */}
                  <div className={styles.moduleDateBlock}>
                    <span className={styles.moduleDateLabel}>Critério de conclusão</span>
                    <select
                      value={mod.criterioConclusao || 'secoes'}
                      onChange={(e) => handleChangeModuloField(idxModulo, 'criterioConclusao', e.target.value)}
                      className={styles.selectInput}
                    >
                      {CRITERIO_CONCLUSAO_OPTIONS.map((opt) => (
                        <option key={opt} value={opt}>
                          {CRITERIO_CONCLUSAO_LABEL[opt] || opt}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className={styles.submitArea}>
          <button type="submit" className={styles.submitButton}>
            {isEditing ? 'Atualizar Trilha' : 'Salvar Trilha'}
          </button>
        </div>

        {/* ================= MODAL DE EDIÇÃO DO MÓDULO ================= */}
        {isModuloModalOpen && activeModuloIndex !== null && activeModulo && (
          <div className={styles.modalOverlay} onMouseDown={closeModuloModal}>
            <div
              className={styles.modalContent}
              onMouseDown={(e) => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
            >
              <div className={styles.modalHeader}>
                <div className={styles.modalTitle}>Editar Módulo {activeModuloIndex + 1}</div>

                <button type="button" className={styles.modalCloseBtn} onClick={closeModuloModal} title="Fechar">
                  <FiX />
                </button>
              </div>

              <div className={styles.modalBody}>
                <aside className={styles.builderToolbar}>
                  {/* (ALTERADO) Botão "Seção" abre picker de tipo de conteúdo inicial */}
                  <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
                    <button
                      type="button"
                      className={styles.toolbarBtn}
                      onClick={() => setIsAddSecaoPickerOpen((v) => !v)}
                      title="Adicionar seção"
                    >
                      <FiPlus />
                      <span>Seção</span>
                    </button>

                    {isAddSecaoPickerOpen && (
                      <div
                        style={{
                          position: 'absolute',
                          top: 'calc(100% + 8px)',
                          left: 0,
                          zIndex: 50,
                          minWidth: 260,
                          background: '#fff',
                          border: '1px solid #e5e7eb',
                          borderRadius: 12,
                          padding: 12,
                          boxShadow: '0 10px 25px rgba(0,0,0,0.15)',
                        }}
                        onMouseDown={(e) => e.stopPropagation()}
                      >
                        <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 8, color: '#002D4C' }}>
                          Tipo de conteúdo inicial
                        </div>

                        <select
                          value={selectedInitialContentType}
                          onChange={(e) => setSelectedInitialContentType(e.target.value)}
                          className={styles.selectInput}
                          style={{ width: '100%', marginBottom: 10 }}
                        >
                          {CONTENT_TYPES.map((t) => (
                            <option key={t} value={t}>
                              {CONTENT_TYPE_LABEL[t]}
                            </option>
                          ))}
                        </select>

                        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                          <button type="button" className={styles.removerBtn} onClick={() => setIsAddSecaoPickerOpen(false)}>
                            Cancelar
                          </button>
                          <button
                            type="button"
                            className={styles.secondaryBtn}
                            onClick={() => {
                              setIsAddSecaoPickerOpen(false);
                              handleAddSecaoWithInitialContent(activeModuloIndex, selectedInitialContentType);
                            }}
                          >
                            Adicionar
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

                  <button
                    type="button"
                    className={styles.toolbarBtn}
                    onClick={() => handleAddPerguntaModulo(activeModuloIndex)}
                    title="Adicionar pergunta ao mini teste"
                  >
                    <FiFileText />
                    <span>Pergunta</span>
                  </button>
                </aside>

                <div className={styles.modalMain}>
                  {/* ===== Dados do módulo ===== */}
                  <div className={styles.moduloCard}>
                    <h3 className={styles.moduloTitle}>Dados do módulo</h3>

                    <label>
                      Título do módulo:
                      <input
                        type="text"
                        value={activeModulo.titulo}
                        onChange={(e) => handleChangeModuloField(activeModuloIndex, 'titulo', e.target.value)}
                        placeholder={`Ex.: Introdução - Módulo ${activeModuloIndex + 1}`}
                        required
                      />
                    </label>

                    <label>
                      Descrição do módulo:
                      <textarea
                        value={activeModulo.descricao}
                        onChange={(e) => handleChangeModuloField(activeModuloIndex, 'descricao', e.target.value)}
                        placeholder="Descrição rápida do conteúdo deste módulo"
                      />
                    </label>

                    <label>
                      Ordem do módulo:
                      <input
                        type="number"
                        value={activeModulo.ordem ?? activeModuloIndex + 1}
                        onChange={(e) => handleChangeModuloField(activeModuloIndex, 'ordem', e.target.value)}
                        className={styles.numberInput}
                      />
                    </label>

                    <label>
                      Teste vinculado (opcional):
                      <select
                        value={activeModulo.idTeste}
                        onChange={(e) => handleChangeModuloField(activeModuloIndex, 'idTeste', e.target.value)}
                        className={styles.selectInput}
                      >
                        <option value="">Nenhum</option>
                        {testes.map((t) => (
                          <option key={t.id} value={t.id}>
                            {t.nome}
                          </option>
                        ))}
                      </select>
                    </label>

                    {/* ✅ NOVO: critério de conclusão dentro do modal também (opcional, mas ajuda) */}
                    <label>
                      Critério de conclusão:
                      <select
                        value={activeModulo.criterioConclusao || 'secoes'}
                        onChange={(e) => handleChangeModuloField(activeModuloIndex, 'criterioConclusao', e.target.value)}
                        className={styles.selectInput}
                      >
                        {CRITERIO_CONCLUSAO_OPTIONS.map((opt) => (
                          <option key={opt} value={opt}>
                            {CRITERIO_CONCLUSAO_LABEL[opt] || opt}
                          </option>
                        ))}
                      </select>
                    </label>
                  </div>

                  {/* ===== MINI TESTE ===== */}
                  <div className={styles.moduloCard}>
                    <h4 className={styles.sectionTitleSmall}>Teste interno do módulo (opcional)</h4>

                    {activeModulo.perguntas.length === 0 && (
                      <p className={styles.mutedNote}>
                        Você pode adicionar perguntas para criar um mini teste específico deste módulo.
                      </p>
                    )}

                    {activeModulo.perguntas.map((p, iPergunta) => (
                      <div key={p.id || iPergunta} className={styles.perguntaCard}>
                        <div className={styles.perguntaHeader}>
                          <span className={styles.perguntaIndex}>Pergunta {iPergunta + 1}</span>
                          <button
                            type="button"
                            className={styles.removerBtn}
                            onClick={() => handleRemovePerguntaModulo(activeModuloIndex, iPergunta)}
                          >
                            Remover pergunta
                          </button>
                        </div>

                        <label>
                          Enunciado:
                          <input
                            type="text"
                            value={p.enunciado}
                            onChange={(e) =>
                              handleChangePerguntaModulo(activeModuloIndex, iPergunta, 'enunciado', e.target.value)
                            }
                            required
                          />
                        </label>

                        <label>
                          Tipo de pergunta:
                          <select
                            value={p.tipo}
                            onChange={(e) =>
                              handleChangePerguntaModulo(activeModuloIndex, iPergunta, 'tipo', e.target.value)
                            }
                            className={styles.selectInput}
                          >
                            {QUESTION_TYPES.map((t) => (
                              <option key={t} value={t}>
                                {QUESTION_TYPE_LABEL[t]}
                              </option>
                            ))}
                          </select>
                        </label>

                        {p.tipo === 'closed' && (
                          <div className={styles.opcoesArea}>
                            <p className={styles.mutedNote}>O participante escolherá uma das opções abaixo.</p>

                            {(p.opcoes || []).map((op, idxOpcao) => (
                              <div key={idxOpcao} className={styles.opcaoRow}>
                                <span className={styles.opcaoLetra}>{op.letra}</span>
                                <input
                                  type="text"
                                  placeholder="Texto da opção"
                                  value={op.texto}
                                  onChange={(e) =>
                                    handleChangeOpcaoModulo(
                                      activeModuloIndex,
                                      iPergunta,
                                      idxOpcao,
                                      'texto',
                                      e.target.value
                                    )
                                  }
                                />
                                <button
                                  type="button"
                                  className={styles.removerOpBtn}
                                  onClick={() => {
                                    setModulos((prev) => {
                                      const arr = [...prev];
                                      const modLocal = { ...arr[activeModuloIndex] };
                                      const perguntas = [...(modLocal.perguntas || [])];
                                      const pLocal = { ...perguntas[iPergunta] };

                                      pLocal.opcoes = [...(pLocal.opcoes || [])];
                                      pLocal.opcoes.splice(idxOpcao, 1);
                                      pLocal.opcoes = reindexLetters(pLocal.opcoes);

                                      perguntas[iPergunta] = pLocal;
                                      modLocal.perguntas = perguntas;
                                      arr[activeModuloIndex] = modLocal;
                                      return arr;
                                    });
                                  }}
                                >
                                  ×
                                </button>
                              </div>
                            ))}

                            <button
                              type="button"
                              className={styles.addSmallBtn}
                              onClick={() => handleAddOpcaoModulo(activeModuloIndex, iPergunta)}
                            >
                              Adicionar opção
                            </button>
                          </div>
                        )}

                        {p.tipo === 'open' && (
                          <p className={styles.mutedNote}>
                            Pergunta aberta: o participante digitará uma resposta livre.
                          </p>
                        )}
                      </div>
                    ))}

                    <button
                      type="button"
                      className={styles.addQuestionBtn}
                      onClick={() => handleAddPerguntaModulo(activeModuloIndex)}
                    >
                      + Adicionar pergunta ao mini teste
                    </button>
                  </div>

                  {/* ===== SEÇÕES E CONTEÚDOS ===== */}
                  <div className={styles.moduloCard}>
                    <h4 className={styles.sectionTitleSmall}>Seções e conteúdos do módulo</h4>

                    {activeModulo.secoes.length === 0 && (
                      <p className={styles.mutedNote}>
                        Adicione seções para dividir o conteúdo deste módulo (ex.: Introdução, Prática, Conclusão...).
                      </p>
                    )}

                    {activeModulo.secoes.map((sec, idxSecao) => (
                      <div key={sec.tempId} className={styles.moduloCard}>
                        <div className={styles.perguntaHeader}>
                          <span className={styles.perguntaIndex}>Seção {idxSecao + 1}</span>
                          <button
                            type="button"
                            className={styles.removerBtn}
                            onClick={() => handleRemoveSecao(activeModuloIndex, idxSecao)}
                          >
                            Remover seção
                          </button>
                        </div>

                        <label>
                          Título da seção:
                          <input
                            type="text"
                            value={sec.titulo}
                            onChange={(e) =>
                              handleChangeSecaoField(activeModuloIndex, idxSecao, 'titulo', e.target.value)
                            }
                            placeholder="Ex.: Introdução, Módulo prático..."
                          />
                        </label>

                        <label>
                          Descrição da seção:
                          <textarea
                            value={sec.descricao}
                            onChange={(e) =>
                              handleChangeSecaoField(activeModuloIndex, idxSecao, 'descricao', e.target.value)
                            }
                            placeholder="Descrição opcional desta seção"
                          />
                        </label>

                        <label>
                          Ordem da seção:
                          <input
                            type="number"
                            value={sec.ordem ?? idxSecao + 1}
                            onChange={(e) =>
                              handleChangeSecaoField(activeModuloIndex, idxSecao, 'ordem', e.target.value)
                            }
                            className={styles.numberInput}
                          />
                        </label>

                        <div className={styles.miniTestArea}>
                          <h5 className={styles.sectionTitleSmall}>Conteúdos da seção</h5>

                          {sec.conteudos.map((cont, idxConteudo) => (
                            <div key={cont.tempId} className={styles.perguntaCard}>
                              <div className={styles.perguntaHeader}>
                                <span className={styles.perguntaIndex}>Conteúdo {idxConteudo + 1}</span>
                                <button
                                  type="button"
                                  className={styles.removerBtn}
                                  onClick={() => handleRemoveConteudo(activeModuloIndex, idxSecao, idxConteudo)}
                                >
                                  Remover conteúdo
                                </button>
                              </div>

                              <label>
                                Tipo de conteúdo:
                                <select
                                  value={cont.tipo}
                                  onChange={(e) =>
                                    handleChangeConteudoField(
                                      activeModuloIndex,
                                      idxSecao,
                                      idxConteudo,
                                      'tipo',
                                      e.target.value
                                    )
                                  }
                                  className={styles.selectInput}
                                >
                                  {CONTENT_TYPES.map((t) => (
                                    <option key={t} value={t}>
                                      {CONTENT_TYPE_LABEL[t]}
                                    </option>
                                  ))}
                                </select>
                              </label>

                              <label>
                                Título (opcional):
                                <input
                                  type="text"
                                  value={cont.titulo}
                                  onChange={(e) =>
                                    handleChangeConteudoField(
                                      activeModuloIndex,
                                      idxSecao,
                                      idxConteudo,
                                      'titulo',
                                      e.target.value
                                    )
                                  }
                                  placeholder="Título curto a ser exibido antes do conteúdo"
                                />
                              </label>

                              {cont.tipo === 'texto' && (
                                <label>
                                  Texto:
                                  <textarea
                                    value={cont.texto}
                                    onChange={(e) =>
                                      handleChangeConteudoField(
                                        activeModuloIndex,
                                        idxSecao,
                                        idxConteudo,
                                        'texto',
                                        e.target.value
                                      )
                                    }
                                    placeholder="Texto desta parte do módulo"
                                  />
                                </label>
                              )}

                              {cont.tipo === 'lista' && (
                                <>
                                  <p className={styles.mutedNote}>
                                    Adicione um item por vez. Eles serão exibidos em lista para o participante.
                                  </p>

                                  <div className={styles.listInputRow}>
                                    <input
                                      type="text"
                                      value={cont.newListItem || ''}
                                      onChange={(e) =>
                                        handleChangeConteudoField(
                                          activeModuloIndex,
                                          idxSecao,
                                          idxConteudo,
                                          'newListItem',
                                          e.target.value
                                        )
                                      }
                                      placeholder="Digite o item e clique em Adicionar"
                                    />
                                    <button
                                      type="button"
                                      className={styles.addSmallBtn}
                                      onClick={() => handleAddListItem(activeModuloIndex, idxSecao, idxConteudo)}
                                    >
                                      Adicionar item
                                    </button>
                                  </div>

                                  {(cont.listaItens || []).length > 0 && (
                                    <ul className={styles.listItemsList}>
                                      {cont.listaItens.map((item, idxItem) => (
                                        <li key={idxItem} className={styles.listItemPill}>
                                          <span className={styles.listItemBullet}>•</span>
                                          <span className={styles.listItemText}>{item}</span>
                                          <button
                                            type="button"
                                            className={styles.listItemRemoveBtn}
                                            onClick={() =>
                                              handleRemoveListItem(
                                                activeModuloIndex,
                                                idxSecao,
                                                idxConteudo,
                                                idxItem
                                              )
                                            }
                                          >
                                            ×
                                          </button>
                                        </li>
                                      ))}
                                    </ul>
                                  )}
                                </>
                              )}

                              {cont.tipo === 'imagem' && (
                                <div className={styles.fileBlock}>
                                  <p className={styles.mutedNote}>
                                    Envie uma imagem que será exibida nesta parte do módulo.
                                  </p>
                                  <div className={styles.fileInputWrapper}>
                                    <input
                                      id={`img-${activeModuloIndex}-${idxSecao}-${idxConteudo}`}
                                      type="file"
                                      accept="image/*"
                                      className={styles.fileInput}
                                      onChange={(e) =>
                                        handleImagemFileChange(activeModuloIndex, idxSecao, idxConteudo, e)
                                      }
                                    />
                                    <label
                                      htmlFor={`img-${activeModuloIndex}-${idxSecao}-${idxConteudo}`}
                                      className={styles.fileLabel}
                                    >
                                      Selecionar imagem
                                    </label>
                                  </div>

                                  {cont.imagemPreview && (
                                    <div className={styles.filePreview}>
                                      <div className={styles.filePreviewImage}>
                                        <img src={cont.imagemPreview} alt="Pré-visualização" />
                                      </div>
                                      <div className={styles.filePreviewInfo}>
                                        <span className={styles.filePreviewLabel}>Pré-visualização</span>
                                        <span className={styles.filePreviewName}>
                                          {cont.imagemFile?.name || (cont.imagemPreview || '').split('/').pop()}
                                        </span>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              )}

                              {cont.tipo === 'link' && (
                                <label>
                                  URL do link:
                                  <input
                                    type="text"
                                    value={cont.urlLink}
                                    onChange={(e) =>
                                      handleChangeConteudoField(
                                        activeModuloIndex,
                                        idxSecao,
                                        idxConteudo,
                                        'urlLink',
                                        e.target.value
                                      )
                                    }
                                    placeholder="https://..."
                                  />
                                </label>
                              )}

                              {cont.tipo === 'video' && (
                                <label>
                                  URL do vídeo (YouTube ou outro player):
                                  <input
                                    type="text"
                                    value={cont.urlVideo}
                                    onChange={(e) =>
                                      handleChangeConteudoField(
                                        activeModuloIndex,
                                        idxSecao,
                                        idxConteudo,
                                        'urlVideo',
                                        e.target.value
                                      )
                                    }
                                    placeholder="https://www.youtube.com/watch?v=..."
                                  />
                                </label>
                              )}

                              {cont.tipo === 'pdf' && (
                                <div className={styles.fileBlock}>
                                  <p className={styles.mutedNote}>
                                    Envie um arquivo PDF que o participante poderá abrir.
                                  </p>
                                  <div className={styles.fileInputWrapper}>
                                    <input
                                      id={`pdf-${activeModuloIndex}-${idxSecao}-${idxConteudo}`}
                                      type="file"
                                      accept="application/pdf"
                                      className={styles.fileInput}
                                      onChange={(e) =>
                                        handlePdfFileChange(activeModuloIndex, idxSecao, idxConteudo, e)
                                      }
                                    />
                                    <label
                                      htmlFor={`pdf-${activeModuloIndex}-${idxSecao}-${idxConteudo}`}
                                      className={`${styles.fileLabel} ${styles.fileLabelGhost}`}
                                    >
                                      Selecionar PDF
                                    </label>
                                  </div>

                                  {(cont.pdfFile || cont.pdfUrl) && (
                                    <div className={styles.filePreview}>
                                      <div className={styles.pdfIcon}>PDF</div>
                                      <div className={styles.filePreviewInfo}>
                                        <span className={styles.filePreviewLabel}>
                                          {cont.pdfFile ? 'Arquivo selecionado' : 'Arquivo atual'}
                                        </span>
                                        <span className={styles.filePreviewName}>
                                          {cont.pdfFile?.name || (cont.pdfUrl || '').split('/').pop()}
                                        </span>
                                        {cont.pdfUrl && (
                                          <a
                                            href={cont.pdfUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className={styles.filePreviewLink}
                                          >
                                            Abrir PDF
                                          </a>
                                        )}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          ))}

                          <button
                            type="button"
                            className={styles.addQuestionBtn}
                            onClick={() => handleAddConteudo(activeModuloIndex, idxSecao)}
                          >
                            + Adicionar conteúdo
                          </button>
                        </div>
                      </div>
                    ))}

                    <button
                      type="button"
                      className={styles.addQuestionBtn}
                      onClick={() => handleAddSecao(activeModuloIndex)}
                    >
                      + Adicionar seção
                    </button>
                  </div>
                </div>
              </div>

              <div className={styles.modalFooter}>
                <button type="button" className={styles.addQuestionBtn} onClick={closeModuloModal}>
                  Concluir edição do módulo
                </button>
              </div>
            </div>
          </div>
        )}
      </form>

      <button
        type="button"
        className={styles.scrollToBottomBtn}
        onClick={() =>
          scrollBottom
            ? window.scrollTo({ top: 0, behavior: 'smooth' })
            : window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' })
        }
        title={scrollBottom ? 'Ir para o topo' : 'Ir para o final'}
      >
        {scrollBottom ? <FaArrowUp /> : <FaArrowDown />}
      </button>
    </div>
  );
}

export default TrilhaFormPage;